import { type ChatCitation } from "@/lib/chat-citations";

export type ChatFastPathKind =
  | "dental_diagnosis"
  | "urgent_clinical_safety"
  | "treatment_outcome"
  | "handoff"
  | "booking";
export type KnowledgeFastPathKind =
  | "price"
  | "hours"
  | "walk_in"
  | "service_availability";

export const CONTACT_US_HREF = "/contact" as const;
export const CONTACT_US_LABEL = "Contact Us" as const;
export const CONTACT_US_HANDOFF_REPLY =
  "I can guide you to our Contact Us form so the team can follow up safely." as const;

export interface ContactUsActionMetadata {
  action: "contact_us";
  href: string;
  label: typeof CONTACT_US_LABEL;
}

export function buildContactUsActionMetadata(href: string | null | undefined = CONTACT_US_HREF): ContactUsActionMetadata {
  const normalizedHref =
    typeof href === "string" && href.trim().length > 0
      ? href.trim()
      : CONTACT_US_HREF;
  const safeHref = /^(?:javascript:|data:)/i.test(normalizedHref) ? CONTACT_US_HREF : normalizedHref;

  return {
    action: "contact_us",
    href: safeHref,
    label: CONTACT_US_LABEL,
  };
}

export const CONTACT_US_ACTION_METADATA: ContactUsActionMetadata = {
  ...buildContactUsActionMetadata(),
};

export interface ChatFastPathReply {
  kind: ChatFastPathKind;
  reply: string;
  metadata?: ContactUsActionMetadata;
}

export interface KnowledgeFastPathReply {
  kind: KnowledgeFastPathKind;
  reply: string;
  citations: ChatCitation[];
  metadata?: ContactUsActionMetadata;
  suppressCitations?: boolean;
}

interface KnowledgeEvidence {
  title: string;
  content: string;
  type?: string;
}

const DENTAL_DIAGNOSIS_PATTERNS: RegExp[] = [
  /\btoothache\b/i,
  /\btooth pain\b/i,
  /\bdental pain\b/i,
  /\bgum pain\b/i,
  /\bjaw pain\b/i,
  /\bdiagnose\b/i,
  /\bdiagnosis\b/i,
  /\bwhat('?s|\s+is)\s+wrong\s+with\s+my\s+tooth\b/i,
  /\bcan you diagnose\b/i,
];

const URGENT_CLINICAL_PATTERNS: RegExp[] = [
  /\bsevere swelling\b/i,
  /\btrouble swallowing\b/i,
  /\bdifficulty swallowing\b/i,
  /\btrouble breathing\b/i,
  /\bdifficulty breathing\b/i,
  /\buncontrolled bleeding\b/i,
];

const TREATMENT_GUARANTEE_PATTERNS: RegExp[] = [
  /\bguarantee\b.*\b(?:treatment|result|results|outcome|outcomes)\b/i,
  /\b(?:treatment|result|results|outcome|outcomes)\b.*\bguarantee\b/i,
  /\bperfect smile\b/i,
];

const HUMAN_HANDOFF_PATTERNS: RegExp[] = [
  /\bspeak to a human\b/i,
  /\btalk to a human\b/i,
  /\bspeak to someone\b/i,
  /\btalk to someone\b/i,
  /\breal person\b/i,
  /\blive agent\b/i,
  /\bhuman agent\b/i,
  /\bteam member\b/i,
  /\bsomeone on your team\b/i,
  /\bcontact a human\b/i,
  /\bhand\s*off\b/i,
  /\bhandoff\b/i,
  /\bi want (?:the )?team to contact me\b/i,
  /\bi(?:'d| would) like (?:the )?team to contact me\b/i,
  /\bcan (?:the )?team contact me\b/i,
  /\bplease have (?:the )?team contact me\b/i,
  /\bhave someone contact me\b/i,
  /\bi need someone to contact me\b/i,
];

const WALK_IN_PATTERNS: RegExp[] = [
  /\bwalk[- ]?ins?\b/i,
  /\bbooking required\b/i,
  /\bcan i just walk in\b/i,
];

const BOOKING_INTENT_PATTERNS: RegExp[] = [
  /\bdo i need to book\b/i,
  /\bbook(?: an?| the)? appointment\b/i,
  /\bschedule(?: an?)? appointment\b/i,
  /\bbook(?: a)? slot\b/i,
  /\breserve\b/i,
  /\bavailability\b/i,
];

const SERVICE_AVAILABILITY_PATTERNS: RegExp[] = [
  /\bdo you offer\b/i,
  /\bdo you provide\b/i,
  /\bdo you do\b/i,
  /\bdo you install\b/i,
  /\bcan i get\b/i,
  /\bdo you have\b/i,
  /\bis .* available\b/i,
];

const SERVICE_PRICE_PATTERNS: RegExp[] = [
  /\bhow much is\b/i,
  /\bhow much does\b/i,
  /\bwhat is the price of\b/i,
  /\bwhat is the cost of\b/i,
  /\bhow much for\b/i,
  /\bprice of\b/i,
  /\bcost of\b/i,
];

const KNOWLEDGE_BASE_MARKER = "--- KNOWLEDGE BASE ---";
const PRICE_MARKER_PATTERN = /(?:£\s?\d[\d,.]*|\b\d+(?:\.\d+)?\s?(?:gbp|pounds?|quid)\b)/i;
const HOURS_MARKER_PATTERN =
  /\b(?:opening hours?|hours?|open(?:ing)?|mon(?:day)?|tue(?:sday)?|wed(?:nesday)?|thu(?:rsday)?|fri(?:day)?|sat(?:urday)?|sun(?:day)?|\d{1,2}:\d{2})\b/i;
const WALK_IN_MARKER_PATTERN =
  /\b(?:walk[- ]?ins?|booking required|capacity allows|book(?:ing)?(?:\s+is)?\s+recommended|appointment required)\b/i;

interface ParsedKnowledgeEntry {
  type: string;
  title: string;
  content: string;
}

type KnowledgeIntent = "price" | "hours" | "walk_in";

type KnowledgeIntentHint =
  | "routine_checkup_price"
  | "hygiene_appointment_price"
  | "general_opening_hours"
  | "weekend_hours";

interface StructuredFaqRow {
  id: string;
  category: string;
  question: string;
  answer: string;
  safety_note: string;
}

interface ServiceDescriptor {
  canonical: string;
  displayLabel: string;
  patterns: RegExp[];
}

const DENTAL_SERVICE_CATALOG: ServiceDescriptor[] = [
  {
    canonical: "routine_checkup",
    displayLabel: "routine check-up",
    patterns: [/\broutine check[- ]?up\b/i, /\broutine checkup\b/i, /\broutine exam\b/i, /\bcheck[- ]?up\b/i],
  },
  {
    canonical: "hygiene_appointment",
    displayLabel: "hygiene appointment",
    patterns: [/\bhygiene appointment\b/i, /\bhygienist\b/i, /\bhygiene\b/i],
  },
  {
    canonical: "teeth_whitening",
    displayLabel: "teeth whitening",
    patterns: [/\bteeth whitening\b/i, /\bcosmetic whitening\b/i, /\bwhitening\b/i],
  },
  {
    canonical: "implants",
    displayLabel: "implants",
    patterns: [/\bdental implants?\b/i, /\bimplants?\b/i],
  },
  {
    canonical: "braces",
    displayLabel: "braces",
    patterns: [/\bbraces\b/i, /\borthodontic braces\b/i],
  },
  {
    canonical: "veneers",
    displayLabel: "veneers",
    patterns: [/\bveneers?\b/i],
  },
  {
    canonical: "crowns",
    displayLabel: "crown",
    patterns: [/\bdental crowns?\b/i, /\bcrowns?\b/i],
  },
  {
    canonical: "root_canal",
    displayLabel: "root canal",
    patterns: [/\broot canal\b/i, /\broot-canal\b/i],
  },
  {
    canonical: "emergency_appointment",
    displayLabel: "emergency appointment",
    patterns: [/\bemergency appointment\b/i, /\bemergency visit\b/i, /\burgent appointment\b/i],
  },
];

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function normalizeText(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function normalizeForScoring(value: string): string {
  return normalizeText(value.toLowerCase().replace(/[_-]+/g, " "));
}

function normalizeHeaderKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function detectRequestedServiceDescriptor(message: string): ServiceDescriptor | null {
  const normalizedMessage = normalizeText(message);
  for (const descriptor of DENTAL_SERVICE_CATALOG) {
    if (descriptor.patterns.some((pattern) => pattern.test(normalizedMessage))) {
      return descriptor;
    }
  }
  return null;
}

function buildRequestedServiceDescriptor(service: string): ServiceDescriptor {
  return {
    canonical: slugify(service) || "requested_service",
    displayLabel: service,
    patterns: [buildServiceRegex(service)],
  };
}

function getRequestedServiceDescriptor(service: string): ServiceDescriptor {
  return detectRequestedServiceDescriptor(service) ?? buildRequestedServiceDescriptor(service);
}

function textMentionsService(text: string, descriptor: ServiceDescriptor): boolean {
  return descriptor.patterns.some((pattern) => pattern.test(text));
}

function textMentionsCompetingService(text: string, descriptor: ServiceDescriptor): boolean {
  return DENTAL_SERVICE_CATALOG.some(
    (candidate) =>
      candidate.canonical !== descriptor.canonical &&
      candidate.patterns.some((pattern) => pattern.test(text))
  );
}

function splitKnowledgeFacts(content: string): string[] {
  return content
    .replace(/\r\n?/g, "\n")
    .split(/\n+/)
    .map((line) => sanitizeFastPathText(line))
    .filter(Boolean);
}

const SECTION_HEADING_PREFIXES: RegExp[] = [
  /^(?:opening hours?|business hours?|hours?)\b(?:\s*[:\-]\s*|\s+)/i,
  /^(?:booking and walk[- ]?ins?|walk[- ]?in policy|walk[- ]?ins?)\b(?:\s*[:\-]\s*|\s+)/i,
  /^(?:pricing|price|fees?|menu|faq|policy|policies|services?)\b(?:\s*[:\-]\s*|\s+)/i,
];

function stripLeadingSectionHeading(value: string): string {
  let result = normalizeText(value).replace(/[=\-_*#]{3,}/g, " ");
  for (const pattern of SECTION_HEADING_PREFIXES) {
    const next = result.replace(pattern, "");
    if (next !== result) {
      result = normalizeText(next);
    }
  }
  return normalizeText(result);
}

const BROAD_KNOWLEDGE_TITLE_PATTERNS: RegExp[] = [
  /\ball topics?\b/i,
  /\bgeneral\b/i,
  /\bbroad\b/i,
  /\bcomplete\b/i,
  /\bcomprehensive\b/i,
  /\bhandbook\b/i,
  /\bmaster\b/i,
  /\blarge\b/i,
  /\blegacy\b/i,
  /\ball[- ]?in[- ]?one\b/i,
  /\bknowledge base\b/i,
];

const INTENT_TITLE_HINTS: Record<KnowledgeIntent, RegExp[]> = {
  price: [
    /\bhygiene\b/i,
    /\bhygienist\b/i,
    /\bpricing and services\b/i,
    /\bservices and pricing\b/i,
    /\bpricing guide\b/i,
    /\bprice list\b/i,
    /\bpricing\b/i,
    /\bprice\b/i,
    /\bcost\b/i,
    /\bfee\b/i,
  ],
  hours: [
    /\bopening hours?\b/i,
    /\bbusiness hours?\b/i,
    /\bhours?\b/i,
    /\bopen(?:ing)?\b/i,
    /\bweekend(?:s)?\b/i,
    /\bsaturday\b/i,
    /\bsunday\b/i,
  ],
  walk_in: [
    /\bbooking and walk[- ]?ins?\b/i,
    /\bwalk[- ]?in policy\b/i,
    /\bwalk[- ]?ins?\b/i,
    /\bbooking\b/i,
  ],
};

function scoreKnowledgeTitle(
  title: string,
  intent: KnowledgeIntent,
  intentHint: KnowledgeIntentHint | null = null
): number {
  const normalizedTitle = normalizeForScoring(title);
  let score = 0;

  for (const hint of INTENT_TITLE_HINTS[intent]) {
    if (hint.test(normalizedTitle)) {
      score += 25;
    }
  }

  if (intent === "price") {
    if (intentHint === "hygiene_appointment_price") {
      if (/\bhygiene\b/i.test(normalizedTitle) || /\bhygienist\b/i.test(normalizedTitle)) score += 20;
      if (/\broutine check[- ]?up\b/i.test(normalizedTitle) || /\bcheck[- ]?up\b/i.test(normalizedTitle)) score -= 8;
    } else if (intentHint === "routine_checkup_price") {
      if (/\broutine check[- ]?up\b/i.test(normalizedTitle) || /\bcheck[- ]?up\b/i.test(normalizedTitle)) score += 20;
      if (/\bhygiene\b/i.test(normalizedTitle) || /\bhygienist\b/i.test(normalizedTitle)) score -= 8;
    }
    if (/\bpricing\b/i.test(normalizedTitle)) score += 15;
    if (/\bprice\b/i.test(normalizedTitle)) score += 12;
    if (/\bcost\b/i.test(normalizedTitle)) score += 10;
    if (/\bfee\b/i.test(normalizedTitle)) score += 10;
    if (/\bservices?\b/i.test(normalizedTitle)) score += 5;
  } else if (intent === "hours") {
    if (intentHint === "weekend_hours") {
      if (/\bweekend(?:s)?\b/i.test(normalizedTitle)) score += 20;
      if (/\bsaturday\b/i.test(normalizedTitle)) score += 15;
      if (/\bsunday\b/i.test(normalizedTitle)) score += 15;
      if (/\bopening hours?\b/i.test(normalizedTitle)) score += 10;
    } else if (intentHint === "general_opening_hours") {
      if (/\bopening hours?\b/i.test(normalizedTitle)) score += 20;
      if (/\bhours?\b/i.test(normalizedTitle)) score += 15;
      if (/\bopen\b/i.test(normalizedTitle)) score += 8;
    }
    if (/\bhours?\b/i.test(normalizedTitle)) score += 15;
    if (/\bopening\b/i.test(normalizedTitle)) score += 12;
    if (/\bopen\b/i.test(normalizedTitle)) score += 8;
    if (/\bweekend(?:s)?\b/i.test(normalizedTitle)) score += 12;
    if (/\bsaturday\b/i.test(normalizedTitle) || /\bsunday\b/i.test(normalizedTitle)) score += 8;
  } else if (intent === "walk_in") {
    if (/\bwalk[- ]?ins?\b/i.test(normalizedTitle)) score += 15;
    if (/\bbooking\b/i.test(normalizedTitle)) score += 8;
  }

  if (/\bfaq\b/i.test(normalizedTitle)) score += 4;
  if (/\bservices?\b/i.test(normalizedTitle)) score += 2;

  for (const broadPattern of BROAD_KNOWLEDGE_TITLE_PATTERNS) {
    if (broadPattern.test(normalizedTitle)) {
      score -= 18;
    }
  }

  return score;
}

function inferKnowledgeIntentHint(message: string): KnowledgeIntentHint | null {
  if (/\bhygiene\b/i.test(message) || /\bhygienist\b/i.test(message)) {
    return "hygiene_appointment_price";
  }

  if (/\broutine check[- ]?up\b/i.test(message) || /\bcheck[- ]?up\b/i.test(message)) {
    return "routine_checkup_price";
  }

  if (
    /\bweekend(?:s)?\b/i.test(message) ||
    /\bsaturday\b/i.test(message) ||
    /\bsunday\b/i.test(message) ||
    /\bopen on saturday\b/i.test(message) ||
    /\bopen on sunday\b/i.test(message) ||
    /\bclosed on saturday\b/i.test(message) ||
    /\bclosed on sunday\b/i.test(message)
  ) {
    return "weekend_hours";
  }

  if (/\bopening hours?\b/i.test(message) || /\bhours?\b/i.test(message) || /\bopen\b/i.test(message)) {
    return "general_opening_hours";
  }

  return null;
}

function scoreIntentText(
  text: string,
  intent: KnowledgeIntent,
  normalizedMessage: string,
  intentHint: KnowledgeIntentHint | null
): number {
  const normalizedText = normalizeForScoring(text);
  let score = 0;

  if (intent === "price") {
    if (/\bprice\b/i.test(normalizedText) || /\bcost\b/i.test(normalizedText) || /\bfee\b/i.test(normalizedText) || /\bpricing\b/i.test(normalizedText)) {
      score += 8;
    }

    if (intentHint === "hygiene_appointment_price") {
      if (/\bhygiene appointment\b/i.test(normalizedText)) score += 45;
      if (/\bhygiene\b/i.test(normalizedText)) score += 40;
      if (/\bhygienist\b/i.test(normalizedText)) score += 30;
      if (/\broutine check[- ]?up\b/i.test(normalizedText) || /\bcheck[- ]?up\b/i.test(normalizedText)) score -= 12;
    } else if (intentHint === "routine_checkup_price") {
      if (/\broutine check[- ]?up\b/i.test(normalizedText)) score += 45;
      if (/\bcheck[- ]?up\b/i.test(normalizedText)) score += 30;
      if (/\bhygiene\b/i.test(normalizedText)) score -= 8;
    } else {
      if (/\broutine check[- ]?up\b/i.test(normalizedText)) score += 18;
      if (/\bhygiene\b/i.test(normalizedText)) score += 18;
    }

    if (/\bhygiene\b/i.test(normalizedMessage) || /\bhygienist\b/i.test(normalizedMessage)) score += 6;
    if (/\broutine check[- ]?up\b/i.test(normalizedMessage) || /\bcheck[- ]?up\b/i.test(normalizedMessage)) score += 6;
  } else if (intent === "hours") {
    if (intentHint === "weekend_hours") {
      if (/\bweekend(?:s)?\b/i.test(normalizedText)) score += 45;
      if (/\bsaturday\b/i.test(normalizedText)) score += 35;
      if (/\bsunday\b/i.test(normalizedText)) score += 35;
      if (/\bclosed\b/i.test(normalizedText)) score += 15;
      if (/\bmon(?:day)?\b/i.test(normalizedText) || /\btue(?:sday)?\b/i.test(normalizedText) || /\bwed(?:nesday)?\b/i.test(normalizedText) || /\bthu(?:rsday)?\b/i.test(normalizedText) || /\bfri(?:day)?\b/i.test(normalizedText)) {
        score -= 8;
      }
    } else {
      if (/\bopening hours?\b/i.test(normalizedText)) score += 40;
      if (/\bhours?\b/i.test(normalizedText)) score += 25;
      if (/\bopen(?:ing)?\b/i.test(normalizedText)) score += 18;
      if (/\bweekday(?:s)?\b/i.test(normalizedText)) score += 10;
      if (/\bweekend(?:s)?\b/i.test(normalizedText) || /\bsaturday\b/i.test(normalizedText) || /\bsunday\b/i.test(normalizedText)) {
        score += 4;
      }
    }

    if (/\bweekend(?:s)?\b/i.test(normalizedMessage) || /\bsaturday\b/i.test(normalizedMessage) || /\bsunday\b/i.test(normalizedMessage)) {
      score += 6;
    }
  } else if (intent === "walk_in") {
    if (/\bbooking\b/i.test(normalizedText)) score += 18;
    if (/\bwalk[- ]?ins?\b/i.test(normalizedText)) score += 35;
    if (/\bbooking required\b/i.test(normalizedText) || /\bbook(?:ing)?\s+is\s+recommended\b/i.test(normalizedText)) score += 22;
    if (/\bcapacity allows\b/i.test(normalizedText)) score += 12;

    if (/\bwalk[- ]?ins?\b/i.test(normalizedMessage)) score += 4;
    if (/\bbooking required\b/i.test(normalizedMessage)) score += 4;
  }

  return score;
}

function sanitizeFastPathText(value: string): string {
  return stripLeadingSectionHeading(value)
    .replace(/\bin this test scenario\b[.!?]*/gi, "")
    .replace(/\s*,\s*,+/g, ", ")
    .replace(/,\s*,+/g, ", ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function splitSentences(content: string): string[] {
  const normalized = sanitizeFastPathText(content);
  if (!normalized) return [];

  return normalized
    .split(/(?<=[.!?])\s+/)
    .map((sentence) => sentence.trim())
    .filter(Boolean);
}

function stripSentencePunctuation(value: string): string {
  return value.replace(/[.?!]+$/g, "").trim();
}

function splitCsvRow(line: string): string[] {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];

    if (inQuotes) {
      if (char === '"') {
        const next = line[i + 1];
        if (next === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
      continue;
    }

    if (char === ",") {
      cells.push(current.trim());
      current = "";
      continue;
    }

    if (char === '"') {
      inQuotes = true;
      continue;
    }

    current += char;
  }

  cells.push(current.trim());
  return cells;
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function parseStructuredFaqRows(content: string): StructuredFaqRow[] {
  const lines = content
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) return [];

  const headerIndex = lines.findIndex((line) => {
    const headers = splitCsvRow(line).map(normalizeHeaderKey);
    return headers.includes("question") && headers.includes("answer");
  });

  if (headerIndex < 0) return [];

  const headers = splitCsvRow(lines[headerIndex]).map(normalizeHeaderKey);
  const questionIndex = headers.indexOf("question");
  const answerIndex = headers.indexOf("answer");
  const categoryIndex = headers.indexOf("category");
  const idIndex = headers.indexOf("id");
  const safetyIndex = headers.indexOf("safetynote");

  if (questionIndex < 0 || answerIndex < 0) return [];

  const rows: StructuredFaqRow[] = [];
  for (let i = headerIndex + 1; i < lines.length; i += 1) {
    const values = splitCsvRow(lines[i]);
    if (values.length === 0) continue;

    rows.push({
      id: idIndex >= 0 ? values[idIndex] || "" : "",
      category: categoryIndex >= 0 ? values[categoryIndex] || "" : "",
      question: values[questionIndex] || "",
      answer: values[answerIndex] || "",
      safety_note: safetyIndex >= 0 ? values[safetyIndex] || "" : "",
    });
  }

  return rows;
}

function formatStructuredAnswer(answer: string): string | null {
  const cleaned = normalizeText(answer);
  if (!cleaned) return null;

  const stripped = stripSentencePunctuation(cleaned).replace(/\bin this test scenario\b[.!?]*/gi, "").replace(/\s{2,}/g, " ").trim();
  if (!stripped) return null;

  return `${stripped.charAt(0).toUpperCase()}${stripped.slice(1)}.`;
}

function scoreStructuredFaqRow(
  row: StructuredFaqRow,
  intent: KnowledgeIntent,
  normalizedMessage: string,
  intentHint: KnowledgeIntentHint | null,
  requestedService: string | null = null
): number {
  const rowText = `${row.question}\n${row.category}\n${row.answer}`;
  const question = normalizeForScoring(row.question);
  const category = normalizeForScoring(row.category);
  const answer = normalizeForScoring(row.answer);
  let score = answer ? 5 : 0;

  if (intent === "price" && requestedService) {
    const descriptor = getRequestedServiceDescriptor(requestedService);
    if (!textMentionsService(rowText, descriptor)) {
      return Number.NEGATIVE_INFINITY;
    }
    if (textMentionsCompetingService(rowText, descriptor)) {
      return Number.NEGATIVE_INFINITY;
    }
  }

  score += scoreIntentText(rowText, intent, normalizedMessage, intentHint);

  if (intent === "price") {
    if (/\bpricing\b/i.test(category) || /\bprice\b/i.test(category)) score += 12;
    if (/\bprice\b/i.test(question) || /\bcost\b/i.test(question) || /\bfee\b/i.test(question) || /\bpricing\b/i.test(question)) {
      score += 10;
    }
    if (/\bcheck[- ]?up\b/i.test(question)) score += 4;
    if (intentHint === "hygiene_appointment_price" && /\bhygiene\b/i.test(question)) score += 10;
    if (intentHint === "routine_checkup_price" && /\broutine check[- ]?up\b/i.test(question)) score += 10;
  } else if (intent === "hours") {
    if (/\bhours?\b/i.test(category)) score += 12;
    if (/\bopening hours?\b/i.test(question) || /\bhours?\b/i.test(question) || /\bopen(?:ing)?\b/i.test(question)) {
      score += 10;
    }
    if (intentHint === "weekend_hours" && (/\bweekend(?:s)?\b/i.test(question) || /\bsaturday\b/i.test(question) || /\bsunday\b/i.test(question))) {
      score += 14;
    }
  } else if (intent === "walk_in") {
    if (/\bbooking\b/i.test(category)) score += 30;
    if (/\bwalk[- ]?ins?\b/i.test(category)) score += 20;
    if (/\bwalk[- ]?ins?\b/i.test(question) || /\bbooking required\b/i.test(question) || /\bcan i just walk in\b/i.test(question)) {
      score += 35;
    }
    if (/\bwalk[- ]?ins?\b/i.test(answer) || /\bbooking\b/i.test(answer)) score += 6;
  }

  if (normalizedMessage.includes("walk") && intent === "walk_in") score += 4;
  if (normalizedMessage.includes("price") && intent === "price") score += 4;
  if (normalizedMessage.includes("hour") && intent === "hours") score += 4;

  return score;
}

function findStructuredFaqAnswer(
  content: string,
  intent: KnowledgeIntent,
  normalizedMessage: string,
  intentHint: KnowledgeIntentHint | null = null,
  requestedService: string | null = null
): string | null {
  const rows = parseStructuredFaqRows(content);
  if (rows.length === 0) return null;

  let bestRow: { row: StructuredFaqRow; score: number } | null = null;
  for (const row of rows) {
    const score = scoreStructuredFaqRow(row, intent, normalizedMessage, intentHint, requestedService);
    if (score <= 0) continue;
    if (!bestRow || score > bestRow.score) {
      bestRow = { row, score };
    }
  }

  return bestRow ? formatStructuredAnswer(bestRow.row.answer) : null;
}

function extractServiceFromMessage(message: string): string | null {
  const detectedService = detectRequestedServiceDescriptor(message);
  if (detectedService) {
    return detectedService.displayLabel;
  }

  const match = message.match(
    /(?:how much is|how much does|what is the price of|what is the cost of|how much for|price of|cost of)\s+(.+?)(?:\?|$)/i
  );
  if (!match?.[1]) return null;

  const service = normalizeText(match[1])
    .replace(/^an?\s+/i, "")
    .replace(/^the\s+/i, "")
    .replace(/\bservice\b$/i, "")
    .trim();

  if (!service) return null;

  if (!/\bhygiene\b/i.test(service) && !/\broutine check[- ]?up\b/i.test(service)) {
    return service.replace(/\bappointment\b$/i, "").trim() || null;
  }

  return service;
}

function extractServiceAvailabilityFromMessage(message: string): string | null {
  const detectedService = detectRequestedServiceDescriptor(message);
  if (detectedService) {
    return detectedService.displayLabel;
  }

  const match = message.match(
    /(?:do you offer|do you provide|do you do|do you install|can i get|do you have)\s+(.+?)(?:\?|$)/i
  );
  if (!match?.[1]) return null;

  const service = normalizeText(match[1])
    .replace(/^an?\s+/i, "")
    .replace(/^the\s+/i, "")
    .replace(/\bservice\b$/i, "")
    .replace(/\btreatment\b$/i, "")
    .trim();

  if (!service) return null;
  if (/\b(?:price|cost|hours?|open|booking|appointment|weekend|walk[- ]?in)\b/i.test(service)) {
    return null;
  }

  return service;
}

function buildServiceRegex(service: string): RegExp {
  const escaped = escapeRegex(service).replace(/\s+/g, "\\s+");
  const suffix = service.trim().toLowerCase().endsWith("s") ? "" : "s?";

  return new RegExp(`\\b${escaped}${suffix}\\b`, "i");
}

function knowledgeExplicitlyMentionsService(
  entries: KnowledgeEvidence[],
  service: string
): boolean {
  const descriptor = getRequestedServiceDescriptor(service);

  return entries.some((entry) => {
    const rows = parseStructuredFaqRows(entry.content);
    if (rows.length > 0) {
      return rows.some((row) => {
        const rowText = `${row.category}\n${row.question}\n${row.answer}`;
        return (
          textMentionsService(rowText, descriptor) &&
          !textMentionsCompetingService(rowText, descriptor)
        );
      });
    }

    return splitKnowledgeFacts(entry.content).some(
      (line) =>
        textMentionsService(line, descriptor) &&
        !textMentionsCompetingService(line, descriptor)
    );
  });
}

function knowledgeHasExplicitServicePrice(
  entries: KnowledgeEvidence[],
  service: string
): boolean {
  const descriptor = getRequestedServiceDescriptor(service);

  return entries.some((entry) => {
    const rows = parseStructuredFaqRows(entry.content);
    if (rows.length > 0) {
      return rows.some((row) => {
        const rowText = `${row.category}\n${row.question}\n${row.answer}`;
        return (
          textMentionsService(rowText, descriptor) &&
          PRICE_MARKER_PATTERN.test(rowText) &&
          !textMentionsCompetingService(rowText, descriptor)
        );
      });
    }

    return splitKnowledgeFacts(entry.content).some(
      (line) =>
        textMentionsService(line, descriptor) &&
        PRICE_MARKER_PATTERN.test(line) &&
        !textMentionsCompetingService(line, descriptor)
    );
  });
}

export function getUnsupportedServiceFallbackReply(
  message: string,
  entries: KnowledgeEvidence[],
  contactUsHref?: string | null
): KnowledgeFastPathReply | null {
  const priceService = extractServiceFromMessage(message);
  if (
    priceService &&
    SERVICE_PRICE_PATTERNS.some((pattern) => pattern.test(message)) &&
    !knowledgeHasExplicitServicePrice(entries, priceService)
  ) {
    return {
      kind: "service_availability",
      reply: `I don’t have that specific ${priceService} price in this assistant’s knowledge base. For non-urgent follow-up, please use Contact Us.`,
      citations: [],
      metadata: buildContactUsActionMetadata(contactUsHref),
      suppressCitations: true,
    };
  }

  const serviceAvailability = extractServiceAvailabilityFromMessage(message);
  if (
    serviceAvailability &&
    SERVICE_AVAILABILITY_PATTERNS.some((pattern) => pattern.test(message)) &&
    !knowledgeExplicitlyMentionsService(entries, serviceAvailability)
  ) {
    return {
      kind: "service_availability",
      reply:
        "I don’t have that specific information in this assistant’s knowledge base. For non-urgent follow-up, please use Contact Us.",
      citations: [],
      metadata: buildContactUsActionMetadata(contactUsHref),
      suppressCitations: true,
    };
  }

  return null;
}

function formatPriceReply(sentence: string, message: string): string | null {
  const cleaned = stripSentencePunctuation(sanitizeFastPathText(sentence));
  const priceMatch = cleaned.match(PRICE_MARKER_PATTERN);
  if (!priceMatch?.[0]) return null;

  const price = normalizeText(priceMatch[0]);
  const service = extractServiceFromMessage(message);
  if (service) {
    if (service.endsWith("s")) {
      return `The price for ${service} is ${price}.`;
    }
    const article = /^[aeiou]/i.test(service) ? "An" : "A";
    return `${article} ${service} is ${price}.`;
  }

  const beforePrice = cleaned.slice(0, priceMatch.index).trim();
  const subject = beforePrice
    .replace(/^(?:the|a|an)\s+/i, "")
    .replace(/\bis\b$/i, "")
    .replace(/[:,-]+$/g, "")
    .trim();

  if (subject) {
    return `${subject.charAt(0).toUpperCase()}${subject.slice(1)} is ${price}.`;
  }

  return `The price is ${price}.`;
}

function formatHoursReply(sentence: string): string | null {
  const cleaned = stripSentencePunctuation(sanitizeFastPathText(sentence));
  const match =
    cleaned.match(/\b(?:is|are)\s+open\s+(.+?)(?:[.!?]|$)/i) ||
    cleaned.match(/\bopen\s+(.+?)(?:[.!?]|$)/i);
  const clause = match?.[1] ? normalizeText(match[1]) : "";

  if (!clause) return null;

  const withComma = clause
    .replace(/,+/g, ",")
    .replace(/\s*,\s*/g, ", ")
    .replace(/\s{2,}/g, " ")
    .trim();
  return `We are open ${withComma}.`;
}

function formatWalkInReply(sentence: string): string {
  const cleaned = stripSentencePunctuation(sanitizeFastPathText(sentence));
  const clause = cleaned
    .replace(/^walk[- ]?ins?(?:\s*[:\-]\s*|\s+)/i, "")
    .replace(/^booking required(?:\s*[:\-]\s*|\s+)/i, "")
    .trim();

  return clause ? `${clause.charAt(0).toUpperCase()}${clause.slice(1)}.` : `${cleaned}.`;
}

function extractKnowledgeEntries(systemPrompt: string | undefined): ParsedKnowledgeEntry[] {
  if (!systemPrompt || !systemPrompt.includes(KNOWLEDGE_BASE_MARKER)) {
    return [];
  }

  const knowledgeSection = systemPrompt.split(KNOWLEDGE_BASE_MARKER)[1] || "";
  const sections = knowledgeSection
    .split(/\n\n(?=\[[A-Z]+\]\s+)/g)
    .map((block) => block.trim())
    .filter(Boolean);

  const entries: ParsedKnowledgeEntry[] = [];

  for (const section of sections) {
    const lines = section.split("\n").map((line) => line.trim()).filter(Boolean);
    if (lines.length < 2) continue;

    const headerMatch = lines[0].match(/^\[(.+?)\]\s+(.+)$/);
    if (!headerMatch) continue;

    entries.push({
      type: headerMatch[1].trim().toLowerCase(),
      title: headerMatch[2].trim(),
      content: lines.slice(1).join("\n").trim(),
    });
  }

  return entries;
}

function citationFromEntry(entry: ParsedKnowledgeEntry): ChatCitation {
  return {
    id: `prompt-${slugify(entry.title) || "source"}`,
    title: entry.title,
    type: entry.type,
    confidence: 1,
  };
}

function entryMatches(entry: ParsedKnowledgeEntry, patterns: RegExp[]): boolean {
  const haystack = `${entry.title}\n${entry.content}`.toLowerCase();
  return patterns.some((pattern) => pattern.test(haystack));
}

function findBestEntry(
  entries: ParsedKnowledgeEntry[],
  patterns: RegExp[],
  keywords: string[],
  intent: KnowledgeIntent,
  normalizedMessage: string,
  intentHint: KnowledgeIntentHint | null = null
): ParsedKnowledgeEntry | null {
  const candidates = entries
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => {
      const haystack = `${entry.title}\n${entry.content}`.toLowerCase();
      return entryMatches(entry, patterns) || keywords.some((keyword) => haystack.includes(keyword));
    });

  if (candidates.length === 0) return null;

  let best: { entry: ParsedKnowledgeEntry; index: number; score: number } | null = null;
  for (const candidate of candidates) {
    const haystack = `${candidate.entry.title}\n${candidate.entry.content}`.toLowerCase();
    const keywordHits = keywords.reduce(
      (count, keyword) => count + (haystack.includes(keyword) ? 1 : 0),
      0
    );
    const patternHits = patterns.reduce(
      (count, pattern) => count + (pattern.test(haystack) ? 1 : 0),
      0
    );
    const score =
      scoreKnowledgeTitle(candidate.entry.title, intent, intentHint) +
      patternHits * 8 +
      keywordHits * 2 -
      candidate.index * 0.001 +
      scoreIntentText(haystack, intent, normalizedMessage, intentHint) * 0.25;

    if (!best || score > best.score) {
      best = { ...candidate, score };
    }
  }

  return best?.entry ?? null;
}

function findBestMatchingSentence(
  content: string,
  patterns: RegExp[],
  intent: KnowledgeIntent,
  normalizedMessage: string,
  intentHint: KnowledgeIntentHint | null = null,
  requestedService: string | null = null
): string | null {
  let bestSentence: { sentence: string; score: number } | null = null;
  const serviceDescriptor = requestedService ? getRequestedServiceDescriptor(requestedService) : null;

  for (const sentence of splitSentences(content)) {
    if (intent === "price" && serviceDescriptor) {
      if (!textMentionsService(sentence, serviceDescriptor)) continue;
      if (textMentionsCompetingService(sentence, serviceDescriptor)) continue;
    }

    const patternHits = patterns.reduce(
      (count, pattern) => count + (pattern.test(sentence) ? 1 : 0),
      0
    );
    if (patternHits === 0) continue;

    const score =
      patternHits * 8 +
      scoreIntentText(sentence, intent, normalizedMessage, intentHint) +
      sentence.length * 0.001;

    if (!bestSentence || score > bestSentence.score) {
      bestSentence = { sentence, score };
    }
  }

  return bestSentence?.sentence ?? null;
}

export function getKnowledgeFastPathReply(
  systemPrompt: string | undefined,
  message: string,
  contactUsHref?: string | null
): KnowledgeFastPathReply | null {
  const normalized = message.toLowerCase();
  const entries = extractKnowledgeEntries(systemPrompt);
  const knowledgeIntentHint = inferKnowledgeIntentHint(normalized);
  const unsupportedServiceFallback = getUnsupportedServiceFallbackReply(
    message,
    entries,
    contactUsHref
  );
  if (entries.length === 0) {
    if (unsupportedServiceFallback) {
      return unsupportedServiceFallback;
    }

    if (WALK_IN_PATTERNS.some((pattern) => pattern.test(normalized))) {
      return {
        kind: "walk_in",
        reply:
          "I do not have a confirmed walk-in policy in the current knowledge base. I can help pass your question to the team.",
        citations: [],
      };
    }
    return null;
  }

  if (unsupportedServiceFallback) {
    return unsupportedServiceFallback;
  }

  const hasWalkInIntent = WALK_IN_PATTERNS.some((pattern) => pattern.test(normalized));
  if (hasWalkInIntent) {
    const walkInEntry = findBestEntry(
      entries,
      [/\bwalk[- ]?ins?\b/i, /\bbooking required\b/i, /\bcapacity allows\b/i, /\bwalk[- ]?in policy\b/i],
      ["walk-in", "booking required", "capacity allows", "reservation"],
      "walk_in",
      normalized
    );

    if (walkInEntry) {
      const walkInStructuredAnswer = findStructuredFaqAnswer(walkInEntry.content, "walk_in", normalized);
      if (walkInStructuredAnswer) {
        return {
          kind: "walk_in",
          reply: walkInStructuredAnswer,
          citations: [citationFromEntry(walkInEntry)],
        };
      }

      const walkInSentence = findBestMatchingSentence(
        walkInEntry.content,
        [WALK_IN_MARKER_PATTERN],
        "walk_in",
        normalized,
        null
      );
      if (walkInSentence) {
        return {
          kind: "walk_in",
          reply: formatWalkInReply(walkInSentence),
          citations: [citationFromEntry(walkInEntry)],
        };
      }
    }

    return {
      kind: "walk_in",
      reply:
        "I do not have a confirmed walk-in policy in the current knowledge base. I can help pass your question to the team.",
      citations: [],
    };
  }

  const requestedPriceService = extractServiceFromMessage(message);
  const hasPriceIntent =
    SERVICE_PRICE_PATTERNS.some((pattern) => pattern.test(normalized)) ||
    /\bhow much\b|\bprice\b|\bcost\b|\bfee\b|\bpricing\b/i.test(normalized);

  if (hasPriceIntent && requestedPriceService) {
    const priceIntentHint =
      knowledgeIntentHint === "hygiene_appointment_price" || knowledgeIntentHint === "routine_checkup_price"
        ? knowledgeIntentHint
        : null;
    const priceEntry = findBestEntry(
      entries,
      [
        ...getRequestedServiceDescriptor(requestedPriceService).patterns,
        /\bprice\b/i,
        /\bcost\b/i,
        /\bfee\b/i,
        /\bpricing\b/i,
      ],
      [requestedPriceService.toLowerCase(), "price", "cost", "fee", "pricing"],
      "price",
      normalized
    );
    if (priceEntry) {
      const structuredAnswer = findStructuredFaqAnswer(
        priceEntry.content,
        "price",
        normalized,
        priceIntentHint,
        requestedPriceService
      );
      if (structuredAnswer) {
        return {
          kind: "price",
          reply: structuredAnswer,
          citations: [citationFromEntry(priceEntry)],
        };
      }

      const priceSentence = findBestMatchingSentence(
        priceEntry.content,
        [
          PRICE_MARKER_PATTERN,
          /\bprice\b/i,
          /\bcost\b/i,
          /\bfee\b/i,
        ],
        "price",
        normalized,
        priceIntentHint,
        requestedPriceService
      );
      const reply = priceSentence ? formatPriceReply(priceSentence, normalized) : null;
      if (reply) {
        return {
          kind: "price",
          reply,
          citations: [citationFromEntry(priceEntry)],
        };
      }
    }
  }

  if (/\bopening hours\b|\bhours\b|\bopen\b|\bwhen are you open\b|\bweekdays\b/i.test(normalized)) {
    const hoursIntentHint =
      knowledgeIntentHint === "weekend_hours" || knowledgeIntentHint === "general_opening_hours"
        ? knowledgeIntentHint
        : null;
    const hoursEntry = findBestEntry(
      entries,
      hoursIntentHint === "weekend_hours"
        ? [/\bweekend\b/i, /\bweekends\b/i, /\bsaturday\b/i, /\bsunday\b/i, /\bclosed\b/i]
        : [/\bopening hours\b/i, /\bhours\b/i, /\bopen\b/i, /\bweekday\b/i],
      ["opening hours", "hours", "open", "weekday"],
      "hours",
      normalized
    );
    if (hoursEntry) {
      const structuredAnswer = findStructuredFaqAnswer(hoursEntry.content, "hours", normalized, hoursIntentHint);
      if (structuredAnswer) {
        return {
          kind: "hours",
          reply: structuredAnswer,
          citations: [citationFromEntry(hoursEntry)],
        };
      }

      const hoursSentence = findBestMatchingSentence(
        hoursEntry.content,
        [
          HOURS_MARKER_PATTERN,
          /\bweekend\b/i,
          /\bweekends\b/i,
          /\bsaturday\b/i,
          /\bsunday\b/i,
          /\bclosed\b/i,
        ],
        "hours",
        normalized,
        hoursIntentHint
      );
      const reply = hoursSentence ? formatHoursReply(hoursSentence) : null;
      if (reply) {
        return {
          kind: "hours",
          reply,
          citations: [citationFromEntry(hoursEntry)],
        };
      }
    }
  }

  return null;
}

function matchesAny(message: string, patterns: RegExp[]): boolean {
  return patterns.some((pattern) => pattern.test(message));
}

export function getDirectChatFastPathReply(
  message: string,
  contactUsHref?: string | null
): ChatFastPathReply | null {
  const normalized = message.toLowerCase();

  if (matchesAny(normalized, URGENT_CLINICAL_PATTERNS)) {
    return {
      kind: "urgent_clinical_safety",
      reply:
        "I can’t assess symptoms here. Severe swelling, trouble breathing, trouble swallowing, or uncontrolled bleeding can need urgent help. Please use emergency or urgent care through the approved local process now.",
    };
  }

  if (matchesAny(normalized, DENTAL_DIAGNOSIS_PATTERNS)) {
    return {
      kind: "dental_diagnosis",
      reply:
        "I can't diagnose symptoms or replace a dentist. Please contact a qualified dental professional. If you have severe swelling, trouble breathing, trouble swallowing, or uncontrolled bleeding, seek urgent or emergency care now.",
    };
  }

  if (matchesAny(normalized, TREATMENT_GUARANTEE_PATTERNS)) {
    return {
      kind: "treatment_outcome",
      reply:
        "I can’t guarantee treatment results. Treatment outcomes depend on a qualified professional’s assessment and individual circumstances. For non-urgent follow-up, please use Contact Us.",
      metadata: buildContactUsActionMetadata(contactUsHref),
    };
  }

  if (matchesAny(normalized, HUMAN_HANDOFF_PATTERNS)) {
    return {
      kind: "handoff",
      reply: CONTACT_US_HANDOFF_REPLY,
      metadata: buildContactUsActionMetadata(contactUsHref),
    };
  }

  if (matchesAny(normalized, BOOKING_INTENT_PATTERNS)) {
    return {
      kind: "booking",
      reply:
        "I can help with general booking questions. For contact details or patient information, please use the clinic’s approved secure process.",
    };
  }

  return null;
}
